/**
 * Chatbot State Manager - LocalStorage Implementation
 * Manages persistent state for the chatbot using localStorage
 * Stores conversation summaries for personalized greetings
 */

import { CookieHandler } from '@/Telemetry/cookie-init';
import { conversationHistoryService } from './conversationHistoryService';

interface ConversationSummary {
  lastIntent?: string;
  lastTopic?: string;
  topics?: string[];
  userType?: string;
  pageContext?: string;
  messageCount?: number;
  averageConfidence?: number;
  lastConversationSummary?: string;
}

interface ChatbotSession {
  sessionId: string;
  userIdentifier: string;
  summary: ConversationSummary;
  isActive: boolean;
  lastActivity: number;
  expiresAt: number;
  createdAt: number;
}

interface ChatbotState {
  greetingShown: boolean;
  currentSessionId: string | null;
  lastPageType: string | null;
  lastContentId: string | null;
  isPrivateSession: boolean;
  temporaryMessages: Array<{
    text: string;
    sender: 'user' | 'bot';
    timestamp: number;
    intentName?: string;
    confidenceScore?: number;
  }>;
}

interface DialogflowResponse {
  queryText?: string;
  intentDisplayName?: string;
  intentDetectionConfidence?: number;
  fulfillmentText?: string;
  languageCode?: string;
  sessionId?: string;
  parameters?: Record<string, unknown>;
  responseId?: string;
}

interface ChatbotMessage {
  text: string;
  sender: 'user' | 'bot';
  dialogflowResponse?: DialogflowResponse;
  intentName?: string;
  confidenceScore?: number;
}

// Type for DFMessenger element with internal message storage
interface DFMessengerWithState extends HTMLElement {
  messages?: Array<{
    type: string;
    name?: string;
    text?: string;
    payload?: string;
  }>;
  shadowRoot: ShadowRoot | null;
}

class ChatbotStateManager {
  private static instance: ChatbotStateManager;
  private readonly SESSION_STORAGE_KEY = 'zngly_chatbot_state';
  private readonly PERSISTENT_STORAGE_KEY = 'zngly_chatbot_sessions';
  private readonly SESSION_EXPIRY_DAYS = 7;
  private state: ChatbotState;

  private constructor() {
    this.state = this.loadState();
    this.cleanupExpiredSessions();
  }

  public static getInstance(): ChatbotStateManager {
    if (!ChatbotStateManager.instance) {
      ChatbotStateManager.instance = new ChatbotStateManager();
    }
    return ChatbotStateManager.instance;
  }

  private loadState(): ChatbotState {
    try {
      const stored = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      // Error loading chatbot state
    }

    return {
      greetingShown: false,
      currentSessionId: null,
      lastPageType: null,
      lastContentId: null,
      isPrivateSession: false,
      temporaryMessages: [],
    };
  }

  private saveState(): void {
    try {
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(this.state));
    } catch (error) {
      // Error saving chatbot state
    }
  }

  /**
   * Load all persistent sessions from localStorage
   */
  private loadSessions(): Record<string, ChatbotSession> {
    try {
      const stored = localStorage.getItem(this.PERSISTENT_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      // Error loading sessions
    }
    return {};
  }

  /**
   * Save sessions to localStorage
   */
  private saveSessions(sessions: Record<string, ChatbotSession>): void {
    try {
      localStorage.setItem(this.PERSISTENT_STORAGE_KEY, JSON.stringify(sessions));
    } catch (error) {
      // Error saving sessions
    }
  }

  /**
   * Clean up expired sessions
   */
  private cleanupExpiredSessions(): void {
    const sessions = this.loadSessions();
    const now = Date.now();
    const cleaned: Record<string, ChatbotSession> = {};

    Object.entries(sessions).forEach(([key, session]) => {
      if (session.expiresAt > now) {
        cleaned[key] = session;
      }
    });

    this.saveSessions(cleaned);
  }

  /**
   * Get user identifier for session tracking
   */
  public getUserIdentifier(): string {
    // Try to get from logged in user first, fallback to client ID
    const loggedInUserId = CookieHandler.getLoggedInUserId();
    const clientId = CookieHandler.getZnClientId();
    return loggedInUserId || clientId || `anonymous-${Date.now()}`;
  }

  /**
   * Get current session from localStorage
   */
  private getCurrentSession(): ChatbotSession | null {
    const sessions = this.loadSessions();
    const userIdentifier = this.getUserIdentifier();

    // Find active session for this user
    const activeSession = Object.values(sessions).find(
      (session) => session.userIdentifier === userIdentifier && session.isActive && session.expiresAt > Date.now()
    );

    return activeSession || null;
  }

  /**
   * Create new session
   */
  private createSession(sessionId: string): ChatbotSession {
    const now = Date.now();
    const expiryTime = now + this.SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    return {
      sessionId,
      userIdentifier: this.getUserIdentifier(),
      summary: {
        messageCount: 0,
        topics: [],
      },
      isActive: true,
      lastActivity: now,
      expiresAt: expiryTime,
      createdAt: now,
    };
  }

  /**
   * Basic session management methods
   */
  public hasGreetingBeenShown(): boolean {
    return this.state.greetingShown;
  }

  public markGreetingAsShown(): void {
    this.state.greetingShown = true;
    this.saveState();
  }

  public resetGreeting(): void {
    this.state.greetingShown = false;
    this.saveState();
  }

  public getSessionId(): string | null {
    return this.state.currentSessionId;
  }

  public setSessionId(id: string): void {
    this.state.currentSessionId = id;
    this.saveState();
  }

  public getCurrentState(): ChatbotState {
    return this.state;
  }

  public updatePageContext(pageType: string, contentId: string | null, isPrivate: boolean): void {
    this.state.lastPageType = pageType;
    this.state.lastContentId = contentId;
    this.state.isPrivateSession = isPrivate;
    this.saveState();
  }

  public shouldStartNewSession(pageType: string, contentId: string | null, isPrivate: boolean): boolean {
    // Always start new session for private content
    if (isPrivate) {
      if (!this.state.isPrivateSession) {
        return true;
      }
      if (contentId !== this.state.lastContentId) {
        return true;
      }
    } else {
      // Moving from private to public - need new session to clear private context
      if (this.state.isPrivateSession) {
        this.state.greetingShown = false;
        this.saveState();
        return true;
      }
    }

    return false;
  }

  public clearState(): void {
    this.state = {
      greetingShown: false,
      currentSessionId: null,
      lastPageType: null,
      lastContentId: null,
      isPrivateSession: false,
      temporaryMessages: [],
    };
    this.saveState();
  }

  /**
   * Load conversation history from localStorage
   */
  public async loadConversationHistory(): Promise<{
    id?: string;
    userIdentifier?: string;
    sessionId?: string;
    lastMessageSummary?: string;
    conversationContext?: string;
    isActive?: boolean;
    lastActivityAt?: string;
    messageCount?: number;
  } | null> {
    try {
      const session = this.getCurrentSession();
      if (!session) return null;

      return {
        id: session.sessionId,
        userIdentifier: session.userIdentifier,
        sessionId: session.sessionId,
        lastMessageSummary: session.summary.lastConversationSummary,
        isActive: session.isActive,
        lastActivityAt: new Date(session.lastActivity).toISOString(),
        messageCount: session.summary.messageCount || 0,
      };
    } catch (error) {
      // Error loading conversation history
      return null;
    }
  }

  /**
   * Create a new session (now just in localStorage)
   */
  public async createServerSession(dialogflowSessionId: string): Promise<boolean> {
    try {
      const sessions = this.loadSessions();
      const userIdentifier = this.getUserIdentifier();

      // Deactivate existing sessions for this user
      Object.values(sessions).forEach((session) => {
        if (session.userIdentifier === userIdentifier) {
          session.isActive = false;
        }
      });

      // Create new session
      const newSession = this.createSession(dialogflowSessionId);
      sessions[dialogflowSessionId] = newSession;

      this.saveSessions(sessions);
      this.setSessionId(dialogflowSessionId);

      return true;
    } catch (error) {
      // Error creating session
      return false;
    }
  }

  /**
   * Log message temporarily (not to localStorage)
   */
  public async logMessage(message: ChatbotMessage): Promise<boolean> {
    try {
      // Store message temporarily in memory during conversation
      this.state.temporaryMessages.push({
        text: message.text,
        sender: message.sender,
        timestamp: Date.now(),
        intentName: message.intentName,
        confidenceScore: message.confidenceScore,
      });

      // Keep only last 50 messages to prevent memory issues
      if (this.state.temporaryMessages.length > 50) {
        this.state.temporaryMessages = this.state.temporaryMessages.slice(-50);
      }

      // Update persistent session with current message count
      const sessionId = this.getSessionId();
      if (sessionId) {
        const sessions = this.loadSessions();
        const session = sessions[sessionId];
        if (session) {
          session.summary.messageCount = this.state.temporaryMessages.length;
          session.lastActivity = Date.now();
          this.saveSessions(sessions);
        }
      }

      this.saveState();
      return true;
    } catch (error) {
      // Error logging message
      return false;
    }
  }

  /**
   * Update session with conversation summary
   */
  public async updateSessionSummary(summary: string, context?: ConversationSummary): Promise<boolean> {
    try {
      const sessions = this.loadSessions();
      const currentSessionId = this.getSessionId();

      if (!currentSessionId || !sessions[currentSessionId]) {
        return false;
      }

      const session = sessions[currentSessionId];
      session.summary.lastConversationSummary = summary;
      session.lastActivity = Date.now();

      if (context) {
        session.summary = { ...session.summary, ...context };

        // Update message count from temporary messages
        session.summary.messageCount = this.state.temporaryMessages.length;

        // Update topics in real-time if lastTopic is provided
        if (context.lastTopic && !session.summary.topics?.includes(context.lastTopic)) {
          session.summary.topics = session.summary.topics || [];
          session.summary.topics.push(context.lastTopic);

          // Keep only last 10 topics
          if (session.summary.topics.length > 10) {
            session.summary.topics = session.summary.topics.slice(-10);
          }
        }
      }

      this.saveSessions(sessions);
      return true;
    } catch (error) {
      // Error updating session
      return false;
    }
  }

  /**
   * Generate conversation summary when session ends using Vertex AI
   */
  public async generateConversationSummary(): Promise<string | null> {
    try {
      const messages = this.state.temporaryMessages;
      if (messages.length === 0) return null;

      // Convert local messages to the format expected by Vertex AI
      const conversationMessages = messages.map((msg) => ({
        message: msg.text,
        source: msg.sender,
      }));

      // Use the conversation history service to generate the summary with Vertex AI
      return await conversationHistoryService.generateConversationSummaryWithVertexAI(conversationMessages);
    } catch (error) {
      // Error generating summary
      return null;
    }
  }

  /**
   * End current session and save summary
   */
  public async endSession(): Promise<void> {
    try {
      const summary = await this.generateConversationSummary();
      if (summary) {
        await this.updateSessionSummary(summary, {
          messageCount: this.state.temporaryMessages.length,
          topics: Array.from(
            new Set(this.state.temporaryMessages.filter((m) => m.intentName).map((m) => m.intentName!))
          ),
        });
      }

      // Clear temporary messages
      this.state.temporaryMessages = [];
      this.saveState();
    } catch (error) {
      // Error ending session
    }
  }

  /**
   * Get conversation context
   */
  public getConversationContext(): ConversationSummary | null {
    const session = this.getCurrentSession();
    return session?.summary || null;
  }

  /**
   * Check if user is returning with conversation history
   */
  public async isReturningUser(): Promise<boolean> {
    try {
      const session = this.getCurrentSession();
      return !!(session?.isActive && session.summary.messageCount && session.summary.messageCount > 0);
    } catch (error) {
      return false;
    }
  }

  /**
   * Get contextual greeting message based on conversation history
   */
  public async getContextualGreeting(): Promise<string | null> {
    try {
      const session = this.getCurrentSession();
      if (session?.isActive && session.summary.lastConversationSummary) {
        return `Welcome back! Last time we were ${session.summary.lastConversationSummary}. What can I help you with today?`;
      }
      return null;
    } catch (error) {
      // Error getting contextual greeting
      return null;
    }
  }

  /**
   * Check if there's already a greeting element in the chat
   */
  public hasGreetingInDOM(): boolean {
    try {
      const df = document.querySelector('df-messenger') as DFMessengerWithState | null;
      if (!df) return false;

      // Check in shadow DOM if accessible
      if (df.shadowRoot) {
        const greetings = df.shadowRoot.querySelectorAll('df-custom-greeting');
        if (greetings.length > 0) return true;

        // Check for greeting in message containers
        const messages = df.shadowRoot.querySelectorAll('.message, .df-message, [class*="message"]');
        for (const msg of messages) {
          if (msg.querySelector('df-custom-greeting')) return true;
          const text = msg.textContent || '';
          if (text.includes('Welcome to') && text.includes('!')) return true;
        }
      }

      // Check using the element's messages property
      if (df.messages && Array.isArray(df.messages)) {
        return df.messages.some((msg) => msg.type === 'custom_template' && msg.name === 'df-custom-greeting');
      }

      return false;
    } catch (error) {
      // Error checking DOM for greeting
      return false;
    }
  }

  /**
   * Clean up on page unload
   */
  public onBeforeUnload(): void {
    // End session and save summary when user leaves
    this.endSession();
  }
}

// Initialize cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    ChatbotStateManager.getInstance().onBeforeUnload();
  });
}

export const chatbotStateManager = ChatbotStateManager.getInstance();
